import type { PaymentOption, PaymentAlert } from '../../shared/schemas/rulesEngine'
import type { EligibilityCheckDoc } from '../types'

type CollectionResultType = NonNullable<EligibilityCheckDoc['collectionResult']>

/**
 * Pick the primary PaymentOption for display/correction purposes — first
 * applicable in-network option, falling back to first in-network, then OON.
 */
export function primaryPaymentOption(
  cr: CollectionResultType | null | undefined,
): PaymentOption | null {
  if (!cr) return null
  const inOptions = cr.inNetwork?.paymentOptions ?? []
  const applicable = inOptions.find(o => o.applicable)
  if (applicable) return applicable
  if (inOptions[0]) return inOptions[0]
  return cr.outOfNetwork?.paymentOptions[0] ?? null
}

/**
 * Normalize a coinsurance fraction to a whole-number percentage string.
 * The rules engine emits coinsurance as a 0..1 fraction; the `> 1` guard
 * tolerates a legacy 0..100 value rather than rendering "2000%".
 */
export function formatPercent(fraction: number): string {
  const pct = fraction > 1 ? fraction : fraction * 100
  return `${Math.round(pct)}%`
}

/**
 * One renderable segment of an option's COLLECT-NOW value (deductible +
 * copay). Coinsurance is deliberately NOT a segment — it's a % of the allowed
 * amount, billed after adjudication and never collected at the desk, so it's
 * surfaced separately via `optionCoinsuranceReference`.
 */
export type OptionAmountPart =
  | { kind: 'amount'; amount: number; label: string }
  | { kind: 'percent'; fraction: number; label: string }
  | { kind: 'text'; text: string; label: string }

/** ObligationTypes for which the unmet deductible is part of the amount due. */
const DEDUCTIBLE_OBLIGATIONS = new Set<string>([
  'copay_plus_deductible',
  'coinsurance_plus_deductible',
  'copay_plus_coinsurance_plus_deductible',
])

/**
 * Break a PaymentOption into the COLLECT-NOW segments the UI renders
 * (deductible + copay). Returns `[]` when there's nothing fixed to collect at
 * the desk — the caller then shows "Nothing to collect now" (when a
 * coinsurance reference exists) or the option's alerts.
 *
 * Deductible is included only when `obligationType` is a `*_plus_deductible`
 * variant: a `copay_only` option may still carry an informational
 * `deductibleRemaining` that is NOT part of what's owed.
 *
 * Coinsurance is NEVER a segment here — it's a % of the allowed amount, billed
 * after adjudication and never collected at the desk. Surface it with
 * `optionCoinsuranceReference`. See the collection philosophy.
 */
export function optionAmountParts(opt: PaymentOption): OptionAmountPart[] {
  if (opt.obligationType === 'capitated') {
    return [{ kind: 'text', text: 'Capitated', label: 'check capitation roster' }]
  }
  if (opt.obligationType === 'oop_max_met') {
    return [{ kind: 'amount', amount: 0, label: 'out-of-pocket maximum met' }]
  }

  const parts: OptionAmountPart[] = []
  const deductibleApplies =
    opt.obligationType !== null && DEDUCTIBLE_OBLIGATIONS.has(opt.obligationType)
  if (deductibleApplies && opt.deductibleRemaining != null) {
    parts.push({
      kind: 'amount',
      amount: opt.deductibleRemaining,
      label: opt.deductibleInheritedFromStc30
        ? 'deductible remaining (plan-level)'
        : 'deductible remaining',
    })
  }
  if (opt.copay != null) {
    parts.push({ kind: 'amount', amount: opt.copay, label: 'copay' })
  }
  // Coinsurance is intentionally omitted — it's a reference, not a collect-now
  // amount (see optionCoinsuranceReference / the collection philosophy).
  return parts
}

/**
 * The coinsurance percentage (0..1) to surface as a REFERENCE — "billed after
 * the claim, not collected today" — or null when the option carries none worth
 * showing. Coinsurance is a % of the allowed amount, which isn't known at the
 * desk, so it's never part of the collect-now amount (see `optionAmountParts`).
 *
 * Returns null for:
 *   - `capitated` / `oop_max_met` — the patient's whole responsibility is
 *     settled by other means ($0 due / provider paid by capitation).
 *   - a 0% coinsurance — nothing is billed later, so there's nothing to note.
 */
export function optionCoinsuranceReference(opt: PaymentOption): number | null {
  if (opt.obligationType === 'capitated' || opt.obligationType === 'oop_max_met') return null
  if (opt.coinsurancePercent == null || opt.coinsurancePercent === 0) return null
  return opt.coinsurancePercent
}

/**
 * True when the collection card should use the stacked option-list layout
 * (case 2) — any network direction returned more than one payment option.
 * False → compact layout (≤ 1 option per network, case 1).
 */
export function isMultiOptionLayout(cr: CollectionResultType): boolean {
  const inCount = cr.inNetwork?.paymentOptions.length ?? 0
  const oonCount = cr.outOfNetwork?.paymentOptions.length ?? 0
  return inCount > 1 || oonCount > 1
}

/** True when the engine produced no payment options at all in either direction. */
export function hasNoOptions(cr: CollectionResultType): boolean {
  const inCount = cr.inNetwork?.paymentOptions.length ?? 0
  const oonCount = cr.outOfNetwork?.paymentOptions.length ?? 0
  return inCount === 0 && oonCount === 0
}

/**
 * True when the card should surface an out-of-network "collect full price"
 * treatment. The plan distinguishes networks and returned in-network
 * cost-sharing but specified NOTHING for out-of-network — the standard
 * reading is that out-of-network care isn't covered, so the patient owes the
 * full visit price out of network.
 *
 * `networkStatus === 'in_network_only'` encodes exactly that: explicit
 * in-network data, no out-of-network options, and NOT the network-unspecified
 * case (W / unknown indicators, where the same cost-sharing applies regardless
 * of network and full-price would be wrong). This is a display-only inference —
 * the engine intentionally leaves `outOfNetwork` null. The OON-count guard is
 * defensive; `in_network_only` already implies it.
 */
export function showsOutOfNetworkFullPrice(cr: CollectionResultType): boolean {
  return (
    cr.networkStatus === 'in_network_only' &&
    (cr.outOfNetwork?.paymentOptions.length ?? 0) === 0
  )
}

/**
 * Partition a network's options into the applicable ones (shown expanded) and
 * the not-applicable ones (collapsed under an expandable toggle).
 */
export function splitApplicable(options: PaymentOption[]): {
  applicable: PaymentOption[]
  notApplicable: PaymentOption[]
} {
  const applicable: PaymentOption[] = []
  const notApplicable: PaymentOption[] = []
  for (const o of options) {
    if (o.applicable) applicable.push(o)
    else notApplicable.push(o)
  }
  return { applicable, notApplicable }
}

/**
 * Drop option-level alerts whose type is already covered by a plan-level
 * alert. The engine deliberately emits prior-auth / referral alerts at both
 * scopes; the card surfaces the plan-level copy once at the top, so the
 * per-option repeat is noise.
 */
export function dedupeOptionAlerts(
  optionAlerts: PaymentAlert[],
  planAlerts: PaymentAlert[],
): PaymentAlert[] {
  const planTypes = new Set(planAlerts.map(a => a.type))
  return optionAlerts.filter(a => !planTypes.has(a.type))
}

/**
 * True when the AI collection result carries a referral_required alert.
 * The alert is produced server-side by alertGenerator from the 271 benefit rows.
 */
export function requiresReferral(
  collectionResult: EligibilityCheckDoc['collectionResult'] | null,
): boolean {
  return Boolean(
    collectionResult?.alerts?.some(alert => alert.type === 'referral_required'),
  )
}

/**
 * Render the plan-level X12 EB04 coverage-level code as a human label.
 * Returns null for IND/FAM/EMP/UNKNOWN/missing — those cases either match the
 * existing per-option IND/FAM badges or carry no information worth surfacing.
 * Used by the slideout's Payer Details grid to disambiguate plans whose
 * coverage doesn't fit the binary IND/FAM split (Employee+Spouse, etc.).
 */
export function planCoverageLabel(planCoverageLevel: string | null | undefined): string | null {
  if (!planCoverageLevel) return null
  const u = planCoverageLevel.toUpperCase().trim()
  switch (u) {
    case 'ESP': return 'Employee + Spouse'
    case 'ECH': return 'Employee + Children'
    case 'CHD': return 'Children only'
    case 'DEP': return 'Dependents only'
    default: return null
  }
}
